import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Loader2, AlertTriangle, Trash2, RotateCcw } from "lucide-react";

export const Route = createFileRoute("/admin/settings")({
  component: SettingsPage,
});

function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const [emailNotif, setEmailNotif] = useState(true);
  const [maintenance, setMaintenance] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [commission, setCommission] = useState("10");
  const [withdrawalFee, setWithdrawalFee] = useState("2");
  const [minVenueBooking, setMinVenueBooking] = useState("10000");
  const [minWithdrawal, setMinWithdrawal] = useState("50");
  const [minTicketPrice, setMinTicketPrice] = useState("3000");
  const [supportEmail, setSupportEmail] = useState("hello@adaptivefuture.com");
  const [clearing, setClearing] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    adminApi.settings()
      .then((s) => {
        setAutoApprove(s.autoApproveVenues);
        setEmailNotif(s.notifyNewBooking);
        setMaintenance(s.maintenanceMode);
        setTestMode(s.testMode ?? false);
        setCommission(String(s.commissionRate * 100));
        setWithdrawalFee(String((s.withdrawalFeeRate ?? 0.02) * 100));
        setMinVenueBooking(String(s.minVenueBookingAmount ?? 10000));
        setMinWithdrawal(String(s.minWithdrawalAmount ?? 50));
        setMinTicketPrice(String(s.minTicketTierPrice ?? 3000));
        setSupportEmail(s.supportEmail);
      })
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const rate = parseFloat(commission) / 100;
      const wRate = parseFloat(withdrawalFee) / 100;
      await adminApi.updateSettings({
        autoApproveVenues: autoApprove,
        notifyNewBooking: emailNotif,
        notifyCancellation: emailNotif,
        maintenanceMode: maintenance,
        commissionRate: rate,
        withdrawalFeeRate: wRate,
        testMode,
        minVenueBookingAmount: parseFloat(minVenueBooking),
        minWithdrawalAmount: parseFloat(minWithdrawal),
        minTicketTierPrice: parseFloat(minTicketPrice),
        supportEmail,
      });
      toast.success("Settings saved successfully");
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div>
        <h1 className="font-display text-2xl font-bold sm:text-3xl">Settings</h1>
        <p className="text-sm text-muted-foreground">Configure global platform behavior.</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg font-semibold">Platform</h2>
          <p className="text-sm text-muted-foreground">General operating switches.</p>
          <div className="mt-5 space-y-5">
            <ToggleRow label="Auto-approve verified hosts" desc="Skip manual moderation for hosts with 5+ approved listings."
              checked={autoApprove} onChange={setAutoApprove} />
            <ToggleRow label="Email notifications" desc="Send admins a daily digest of bookings and disputes."
              checked={emailNotif} onChange={setEmailNotif} />
            <ToggleRow label="Maintenance mode" desc="Temporarily disable new bookings across the site."
              checked={maintenance} onChange={setMaintenance} danger />
            <ToggleRow label="Test mode" desc="Lowers minimum venue booking to ₦10, ticket price to ₦10, and withdrawal to ₦10 for testing. Disabling restores minimums to ₦10,000 (venue), ₦3,000 (ticket), and ₦50 (withdrawal)."
              checked={testMode} onChange={(v) => {
                setTestMode(v);
                if (v) {
                  setMinVenueBooking("10");
                  setMinTicketPrice("10");
                  setMinWithdrawal("10");
                } else {
                  setMinVenueBooking("10000");
                  setMinTicketPrice("3000");
                  setMinWithdrawal("50");
                }
              }} />
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-soft">
          <h2 className="font-display text-lg font-semibold">Finance & support</h2>
          <p className="text-sm text-muted-foreground">Commission and contact details.</p>
          <div className="mt-5 space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="commission">Platform commission</Label>
                <span className="text-sm font-bold text-primary">{commission}%</span>
              </div>
              <input
                id="commission"
                type="range"
                min={0}
                max={50}
                step={5}
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                className="mt-3 w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted accent-primary"
              />
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>0%</span>
                <span>10%</span>
                <span>20%</span>
                <span>30%</span>
                <span>40%</span>
                <span>50%</span>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="withdrawalFee">Withdrawal platform fee</Label>
                <span className="text-sm font-bold text-primary">{withdrawalFee}%</span>
              </div>
              <input
                id="withdrawalFee"
                type="range"
                min={0}
                max={20}
                step={1}
                value={withdrawalFee}
                onChange={(e) => setWithdrawalFee(e.target.value)}
                className="mt-3 w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted accent-primary"
              />
              <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                <span>0%</span>
                <span>5%</span>
                <span>10%</span>
                <span>15%</span>
                <span>20%</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Deducted from each withdrawal request before payout.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label htmlFor="minVenueBooking">Min venue booking (₦)</Label>
                <Input id="minVenueBooking" type="number" min={0} step={10} value={minVenueBooking}
                  onChange={(e) => setMinVenueBooking(e.target.value)}
                  disabled={testMode}
                  className="mt-1.5"
                  placeholder={testMode ? "Controlled by test mode" : ""} />
              </div>
              <div>
                <Label htmlFor="minWithdrawal">Min withdrawal (₦)</Label>
                <Input id="minWithdrawal" type="number" min={0} step={10} value={minWithdrawal}
                  onChange={(e) => setMinWithdrawal(e.target.value)}
                  disabled={testMode}
                  className="mt-1.5"
                  placeholder={testMode ? "Controlled by test mode" : ""} />
              </div>
              <div>
                <Label htmlFor="minTicketPrice">Min ticket price (₦)</Label>
                <Input id="minTicketPrice" type="number" min={0} step={10} value={minTicketPrice}
                  onChange={(e) => setMinTicketPrice(e.target.value)}
                  disabled={testMode}
                  className="mt-1.5"
                  placeholder={testMode ? "Controlled by test mode" : ""} />
              </div>
            </div>
            <div>
              <Label htmlFor="support">Support email</Label>
              <Input id="support" type="email" value={supportEmail} onChange={(e) => setSupportEmail(e.target.value)} className="mt-1.5" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
              {saving ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Save changes
            </Button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="rounded-2xl border-2 border-destructive/30 bg-destructive/5 p-6 shadow-soft">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-5 text-destructive" />
          <h2 className="font-display text-lg font-semibold text-destructive">Danger Zone</h2>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Irreversible operations. Proceed with extreme caution.</p>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {/* Reset Financial Data */}
          <div className="rounded-xl border border-border/60 bg-card p-5">
            <div className="flex items-center gap-2">
              <RotateCcw className="size-4 text-amber-600" />
              <h3 className="text-sm font-semibold">Reset Financial Data</h3>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Deletes all wallets, transactions, escrows, payments, refunds, and withdrawals. Resets booking payment statuses to pending. Preserves venues, bookings, events, and users.
            </p>
            <Button
              variant="outline"
              className="mt-4 w-full gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
              disabled={resetting}
              onClick={() => {
                const confirmed = confirm(
                  "Are you sure you want to reset ALL financial data?\n\n" +
                  "This will delete:\n" +
                  "• All wallets and ledger entries\n" +
                  "• All transactions and escrows\n" +
                  "• All payments, refunds, and withdrawals\n\n" +
                  "Bookings, venues, events, and users will be preserved.\n" +
                  "This action is IRREVERSIBLE."
                );
                if (!confirmed) return;
                setResetting(true);
                adminApi.resetFinancialData()
                  .then((res) => toast.success(res.message))
                  .catch((err) => {
                    const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Reset failed";
                    toast.error(msg);
                  })
                  .finally(() => setResetting(false));
              }}
            >
              {resetting ? <Loader2 className="size-4 animate-spin" /> : <RotateCcw className="size-4" />}
              Reset Financial Data
            </Button>
          </div>

          {/* Clear All System Data */}
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-5">
            <div className="flex items-center gap-2">
              <Trash2 className="size-4 text-destructive" />
              <h3 className="text-sm font-semibold text-destructive">Clear All System Data</h3>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Wipes everything — venues, bookings, events, transactions, wallets, reviews, images, and all user accounts except the SuperAdmin. This is a full factory reset.
            </p>
            <Button
              variant="outline"
              className="mt-4 w-full gap-2 border-destructive/40 text-destructive hover:bg-destructive/10"
              disabled={clearing}
              onClick={() => {
                const confirmed = confirm(
                  "⚠️ WARNING: This will DELETE EVERYTHING in the system.\n\n" +
                  "All venues, bookings, events, transactions, wallets, reviews,\n" +
                  "uploaded images, and ALL user accounts (except SuperAdmin)\n" +
                  "will be permanently deleted.\n\n" +
                  "This action is IRREVERSIBLE. Type DELETE to confirm:"
                );
                if (!confirmed) return;
                const typed = prompt("Type DELETE to confirm:");
                if (typed !== "DELETE") {
                  toast.error("Confirmation cancelled — you must type DELETE exactly.");
                  return;
                }
                setClearing(true);
                adminApi.clearSystem()
                  .then((res) => {
                    toast.success(
                      `System cleared: ${res.deletedUsers} users, ${res.deletedVenues} venues, ${res.deletedBookings} bookings, ${res.deletedEvents} events deleted.`
                    );
                  })
                  .catch((err) => {
                    const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Clear failed";
                    toast.error(msg);
                  })
                  .finally(() => setClearing(false));
              }}
            >
              {clearing ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
              Clear All System Data
            </Button>
          </div>
        </div>
      </div>

    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange, danger }: { label: string; desc: string; checked: boolean; onChange: (v: boolean) => void; danger?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className={`text-sm font-semibold ${danger ? "text-destructive" : ""}`}>{label}</div>
        <div className="mt-0.5 text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={(v) => { onChange(v); toast.success(`${label} ${v ? "enabled" : "disabled"}`); }} />
    </div>
  );
}

